import React, { useState } from 'react';
import Select from 'react-select';
import { SelectOption } from 'lib/components/types';
import classes from './index.module.scss';
import { Vendor } from 'types';

/**
 * Props interface for the VendorSelect component
 */
interface Props {
	/** List of vendors to populate the dropdown */
	vendors: Vendor[];
	/** Currently selected vendor ID */
	value?: number;
	/** Callback function to update the selected vendor ID */
	setValue: React.Dispatch<React.SetStateAction<number | undefined>>;
}

/**
 * VendorSelect component
 * Renders a searchable dropdown for selecting vendors
 * Uses react-select for enhanced dropdown functionality
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const VendorSelect = ({ value, setValue, vendors }: Props) => {
	// Transform vendor data into format required by react-select
	const options: SelectOption<number>[] = vendors.map(({ id, name }) => ({
		value: id,
		label: name,
	}));

	// Track the currently selected option object
	const [option, setOption] = useState<SelectOption<number> | null>(
		typeof value !== undefined ? options.find((option) => option.value === value)! : null
	);

	/**
	 * Handle selection change in the dropdown
	 * Updates both the local state and parent component state
	 *
	 * @param {SelectOption<number> | null} option - The newly selected option or null if cleared
	 */
	const handleChange = (option: SelectOption<number> | null) => {
		setOption(option);
		setValue(option?.value);
	};

	return (
		<Select
			placeholder="Assign a Vendor"
			className={classes['react-select-container']}
			classNamePrefix="react-select"
			options={options}
			isClearable={true}
			isSearchable={true}
			value={option}
			onChange={handleChange}
		/>
	);
};

export default VendorSelect;
